package uz.mint.frontend.components.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import uz.mint.frontend.ShellStyle
import uz.mint.frontend.SiteConfig
import uz.mint.frontend.flexWrapCss
import uz.mint.frontend.i18n.SiteStrings
import uz.mint.frontend.toSitePalette

/**
 * The dark strip above the nav, carrying the support line.
 *
 * This is the same strip that was removed on 2026-08-04, back for the opposite reason. It went
 * because everything it held was invented — a promo code no checkout accepts, beside a phone
 * number (+998 71 200 00 20) that came from the design and rang nowhere. A support line that does
 * not answer is worse than no line at all, so the whole band went rather than half of it.
 *
 * The number here is real, and it is the same one the public offer and the privacy policy print —
 * [SiteConfig.PHONE] is the single source, because a header and a legal document that disagree
 * about how to reach a company is a contradiction a customer notices.
 *
 * It is a `tel:` link rather than text: on the phone most of these visitors are holding, a number
 * you cannot tap is a number you have to copy by hand.
 */
@Composable
fun TopBar(strings: SiteStrings) {
    val palette = ColorMode.current.toSitePalette()

    Div(
        Modifier
            .fillMaxWidth()
            .backgroundColor(palette.band)
            .color(palette.bandFg)
            .fontSize(13.px)
            .letterSpacing(0.01.em)
            .toAttrs()
    ) {
        Row(
            ShellStyle.toModifier().padding(topBottom = 9.px).gap(10.px).flexWrapCss(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(strings.topPhoneLabel, Modifier.opacity(0.75))
            // **The colour is set here on purpose, and leaving it out made the number vanish.**
            // `UncoloredLinkVariant` does not mean "inherit": it emits `color: var(--silk-color)`,
            // the theme's own ink. This strip is dark in both modes, so in the light theme the
            // number was drawn in #0F2620 on a #0F2620 band — present, tappable and invisible,
            // while the label beside it stayed readable because a span really does inherit.
            //
            // Mint rather than the band's foreground: it is the one colour that is identical in
            // both palettes, and a support line is meant to be found.
            Link(
                path = SiteConfig.PHONE_HREF,
                modifier = Modifier
                    .color(palette.mint)
                    .fontWeight(com.varabyte.kobweb.compose.css.FontWeight.Bold),
                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
            ) { SpanText(SiteConfig.PHONE) }
        }
    }
}
